import React, { useState, useEffect } from 'react';
import { Search, Download, CreditCard, Filter, CheckCircle, Clock, Printer } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/api/api';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';

const ICardReport = () => {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [statistics, setStatistics] = useState({ total: 0, printed: 0, pending: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    course_id: '',
    session: '',
    status: 'all'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [filters.course_id, filters.session, filters.status]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.course_id) params.append('course_id', filters.course_id);
      if (filters.session) params.append('session', filters.session);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      
      const response = await api.get(`/icard-builder/report?${params.toString()}`);
      const data = response.data;
      
      setStudents(data.students || []);
      setCourses(data.courses || []);
      setSessions(data.sessions || []);
      setStatistics(data.statistics || { total: 0, printed: 0, pending: 0 });
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = 
      (student.full_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (student.admission_number?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const exportToCSV = () => {
    if (filteredStudents.length === 0) {
      toast.error('No data available for export');
      return;
    }
    
    const headers = ['Admission Number', 'Student Name', 'Course', 'Session', 'I-Card Status', 'Print Count', 'Last Printed', 'Printed By'];
    const csvData = filteredStudents.map(student => [
      student.admission_number || '',
      student.full_name || '',
      student.course_name || '',
      student.session || '',
      student.icard_status || 'Pending',
      student.icard_print_count || 0,
      student.icard_last_printed ? new Date(student.icard_last_printed).toLocaleString('en-IN') : '',
      student.icard_last_printed_by || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `icard_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('CSV exported successfully');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">I-Card Report</h1>
          <p className="text-sm text-gray-500 mt-1">View I-Card print status and reports</p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/admission/print-icard"
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Printer className="h-4 w-4" />
            Print I-Card
          </Link>
          <button
            onClick={exportToCSV}
            disabled={filteredStudents.length === 0}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-300"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Students</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{statistics.total}</p>
            </div>
            <CreditCard className="h-12 w-12 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">I-Card Printed</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{statistics.printed}</p>
            </div>
            <CheckCircle className="h-12 w-12 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-3xl font-bold text-orange-600 mt-1">{statistics.pending}</p>
            </div>
            <Clock className="h-12 w-12 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or Admission No..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={filters.course_id}
              onChange={(e) => setFilters({ ...filters, course_id: e.target.value })}
            >
              <option value="">All Courses</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={filters.session}
              onChange={(e) => setFilters({ ...filters, session: e.target.value })}
            >
              <option value="">All Sessions</option>
              {sessions.map(session => (
                <option key={session} value={session}>{session}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="all">All Status</option>
              <option value="printed">Printed</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admission No.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Session</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">I-Card Status</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Print Count</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Printed</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">No students found</td>
                </tr>
              ) : (
                filteredStudents.map((student) => (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                          {(() => {
                            const photo = student.photo?.data || (typeof student.photo === 'string' ? student.photo : null)
                              || student.passport_photo?.data || (typeof student.passport_photo === 'string' ? student.passport_photo : null)
                              || student.documents?.passport_photo?.data || (typeof student.documents?.passport_photo === 'string' ? student.documents.passport_photo : null);
                            return photo ? (
                              <img src={photo} className="w-full h-full object-cover" alt="" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">N/A</div>
                            );
                          })()}
                        </div>
                        <span className="font-medium text-gray-900">{student.full_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{student.admission_number}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{student.course_name || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{student.session || '-'}</td>
                    <td className="px-6 py-4 text-center">
                      {(student.icard_print_count || 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3" />
                          Printed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                          <Clock className="h-3 w-3" />
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="font-semibold text-gray-900">{student.icard_print_count || 0}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {student.icard_last_printed ? (
                        <div>
                          <p>{formatDate(student.icard_last_printed)}</p>
                          <p className="text-xs text-gray-400">{student.icard_last_printed_by}</p>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ICardReport;
